import React, { FormEvent } from 'react';
import { Stepper } from '@op/opds.opux-stepper';
import { space } from '@op/opds.tokens';
import { Checkbox, CheckboxGroup, Form, FormRow } from '@op/opds.opux';
import classes from './Selections.module.scss';
import { useIsMobile } from '../../../util/mediaQuery';

interface InnerOptionItem {
  name: JSX.Element;
  share: number;
}

interface OptionItem {
  id: number;
  name: JSX.Element;
  description?: JSX.Element;
  icon?: JSX.Element;
  instruments: InnerOptionItem[];
  locations: InnerOptionItem[];
  fields: InnerOptionItem[];
}

interface SelectedItem extends OptionItem {
  investment: number;
}

interface Props {
  selected: SelectedItem[];
  setSelected: React.Dispatch<React.SetStateAction<SelectedItem[]>>;
  options: OptionItem[];
  type: 'STOCK' | 'FUND';
}

const Selections = ({ selected, setSelected, options, type }: Props): JSX.Element => {
  const isMobile = useIsMobile();
  // Check if this object is selected already
  const isSelected = (idOfObject: number): boolean => {
    return selected.some((element: SelectedItem) => element.id === idOfObject);
  };

  // Get value based on name
  const getValue = (id: number): number => {
    const thisObject = selected.find((element: SelectedItem) => element.id === id);
    return thisObject ? thisObject.investment : 0;
  };

  // All names in selected
  const selectedIds: number[] = selected.map((element: SelectedItem) => element.id);

  // Event handlers
  const handleAddOrDelete = (id: number): void => {
    if (isSelected(id)) {
      // Toggle remove
      const filtered = selected.filter((element: SelectedItem) => element.id !== id);
      setSelected(filtered);
    } else {
      // Toggle add
      const thisObject = options.find((element: OptionItem) => element.id === id);
      if (thisObject) {
        const newItem: SelectedItem = {
          ...thisObject,
          investment: type === 'STOCK' ? 1000 : 500,
        };
        setSelected([...selected, newItem]);
      }
    }
  };

  const handleChange = (value: number | number[], id: number): void => {
    const copy = [...selected];
    const thisObject = copy.find((element: SelectedItem) => element.id === id);
    if (thisObject) {
      thisObject.investment = value as number;
      setSelected(copy);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
  };

  return (
    <Form stacked spacing="small" className={classes.form} onSubmit={handleSubmit}>
      <FormRow className={classes.formRow}>
        <CheckboxGroup>
          {options.map((element) => (
            <div className={classes.checkboxContainer} key={element.id}>
              <div style={{ padding: space.small }}>
                <Checkbox
                  data-testid={`course7-task-checkbox-${element.id}`}
                  onClick={() => handleAddOrDelete(element.id)}
                  checked={selectedIds.includes(element.id)}
                  labelProps={{
                    style: { alignItems: 'center' },
                  }}
                >
                  {!isMobile && element.icon && (
                    <div
                      style={{
                        marginRight: space.xs,
                        display: 'flex',
                      }}
                    >
                      {element.icon}
                    </div>
                  )}
                  <div>
                    {element.name}
                    {element.description && (
                      <div className={classes.description}>{element.description}</div>
                    )}
                  </div>
                </Checkbox>
              </div>

              {selectedIds.includes(element.id) && (
                <div className={classes.stepperContainer}>
                  <Stepper
                    className={classes.stepper}
                    step={500}
                    min={type === 'STOCK' ? 1000 : 500}
                    max={3000}
                    value={getValue(element.id)}
                    unit="€"
                    onChange={(value) => handleChange(value, element.id)}
                  />
                </div>
              )}
            </div>
          ))}
        </CheckboxGroup>
      </FormRow>
    </Form>
  );
};

export default Selections;
